import classNames from 'classnames';
import React, { useState, useRef } from 'react';

import { format as dateFormat } from '@eika-infrastruktur/date';
import { postMessageRN, runningInRNWebView, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { IconButton, LinkButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';

import { getDateFromTwelveMonthsAgo, getDateTwelveMonthsInTheFuture } from '..';
import type { AccountId, SearchTransactionsRequest } from '../../../../generated-types';
import useDetectClickOutside from '../../../../hooks/useDetectClickOutside';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { getTransactionListDownloadUrl } from '../../../../services/accountService';
import nullableZonedDateToUtc from '../../../../utils/mapper/nullableZonedDateToUtc';

import './DownloadTransactionsContextMenu.scss';

type DownloadTransactionsContextMenuProps = {
    searchParams?: SearchTransactionsRequest;
    accountId?: AccountId;
};

const DATE_FORMAT = 'dd-MM-yy';

const getTitle = (fromDate?: string, toDate?: string) => {
    if (!fromDate || !toDate) {
        return 'Transaksjonsliste';
    }
    return `Transakasjonsliste-${dateFormat(fromDate, DATE_FORMAT)}-${dateFormat(toDate, DATE_FORMAT)}`;
};
const DownloadTransactionsContextMenu: React.FC<DownloadTransactionsContextMenuProps> = ({
    searchParams,
    accountId,
}) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const [contextMenuIsOpen, setContextMenuIsOpen] = useState(false);

    const translation = useTranslation();

    useDetectClickOutside(wrapperRef, () => setContextMenuIsOpen(false), contextMenuIsOpen);

    const downloadParams = searchParams || {
        toDate: nullableZonedDateToUtc(getDateTwelveMonthsInTheFuture()),
        fromDate: nullableZonedDateToUtc(getDateFromTwelveMonthsAgo()),
    };

    const pdfLink = downloadParams && accountId ? getTransactionListDownloadUrl(accountId, downloadParams, 'PDF') : '';
    const excelDownloadUrl =
        downloadParams && accountId ? getTransactionListDownloadUrl(accountId, downloadParams, 'EXCEL') : '';

    const csvDownloadUrl =
        downloadParams && accountId ? getTransactionListDownloadUrl(accountId, downloadParams, 'CSV') : '';

    const openPdf = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (pdfLink.length === 0) return;

        if (runningInRNWebView) {
            postMessageRN({
                action: WebToRNAction.OPEN_PDF,
                payload: {
                    url: pdfLink,
                    title: getTitle(downloadParams?.fromDate, downloadParams?.toDate),
                },
            });
        } else {
            window.open(pdfLink, 'menubar=no,location=no,resizable=yes,scrollbars=yes,status=yes,noopener');
        }
    };

    return downloadParams && accountId ? (
        <section className="download-transactions-button__wrapper" ref={wrapperRef}>
            <IconButton
                className={classNames('download-transactions-button', { active: contextMenuIsOpen })}
                icon={IconTypes.NEDLASTING_24}
                screenReaderText={format(
                    translation.transactionDownload.downloadMenu,
                    contextMenuIsOpen ? translation.transactionDownload.close : translation.transactionDownload.open,
                )}
                aria-expanded={contextMenuIsOpen}
                onClick={() => setContextMenuIsOpen((prev) => !prev)}
                onBlur={(e) => {
                    if (
                        !wrapperRef.current?.contains(e.relatedTarget as Element) &&
                        !dialogRef.current?.contains(e.relatedTarget as Element)
                    ) {
                        setContextMenuIsOpen(false);
                    }
                }}
                data-testid="download-transactions-button"
            />

            {contextMenuIsOpen && (
                <div
                    role="dialog"
                    className="download-transactions-button__context-menu"
                    ref={dialogRef}
                    onBlur={(e) => {
                        if (!dialogRef.current?.contains(e.relatedTarget as Element)) {
                            setContextMenuIsOpen(false);
                        }
                    }}
                >
                    <LinkButton className="download-transactions-button__button-element" onClick={openPdf}>
                        {translation.transactionDownload.exportToPdf}
                    </LinkButton>
                    {!runningInRNWebView && (
                        <Link
                            onClick={(e) => e.stopPropagation()}
                            href={excelDownloadUrl}
                            className="download-transactions-button__button-element"
                        >
                            {translation.transactionDownload.exportToExcel}
                        </Link>
                    )}
                    {!runningInRNWebView && (
                        <Link
                            onClick={(e) => e.stopPropagation()}
                            href={csvDownloadUrl}
                            className="download-transactions-button__button-element"
                        >
                            {translation.transactionDownload.exportToCSV}
                        </Link>
                    )}
                </div>
            )}
        </section>
    ) : null;
};

export default DownloadTransactionsContextMenu;
